// Archivo para manejar la carga de listas de códigos de acción y resultados
//
import React, { useState, useEffect } from 'react'
import {
  Typography,
  Button,
  Alert,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { Edit, Delete, UploadFile, Download } from '@mui/icons-material'
import api from '../../services/api'
import PrivatePageSection from '../../components/layout/PrivatePageSection'

const CodesManagement = () => {
  const [actionCodes, setActionCodes] = useState([])
  const [resultCodes, setResultCodes] = useState([])
  const [contactCodes, setContactCodes] = useState([])
  const [fileA, setFileA] = useState(null)
  const [fileR, setFileR] = useState(null)
  const [fileC, setFileC] = useState(null)
  const [msg, setMsg] = useState({ type: '', text: '' })

  const [openDialog, setOpenDialog] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editingType, setEditingType] = useState('action')
  const [current, setCurrent] = useState({ id: null, code: '', label: '' })

  // — Fetch inicial de listas
  useEffect(() => {
    fetchActionCodes()
    fetchResultCodes()
    fetchContactCodes()
  }, [])

  const fetchActionCodes = async () => {
    try {
      const { data } = await api.get('/codes/actions')
      setActionCodes(data)
      setMsg({ type: '', text: '' })
    } catch {
      setMsg({ type: 'error', text: 'Error cargando códigos de acción' })
    }
  }

  const fetchResultCodes = async () => {
    try {
      const { data } = await api.get('/codes/results')
      setResultCodes(data)
      setMsg({ type: '', text: '' })
    } catch {
      setMsg({ type: 'error', text: 'Error cargando códigos de resultado' })
    }
  }

  const fetchContactCodes = async () => {
    try {
      const { data } = await api.get('/codes/contacts')
      setContactCodes(data)
      setMsg({ type: '', text: '' })
    } catch {
      setMsg({ type: 'error', text: 'Error cargando códigos de contacto' })
    }
  }

  // — Subida masiva
  const handleUpload = async (endpoint, file) => {
    setMsg({ type: '', text: '' })
    if (!file) {
      setMsg({ type: 'error', text: 'Selecciona un archivo primero.' })
      return
    }
    const form = new FormData()
    form.append('file', file)
    try {
      const res = await api.post(`/codes/${endpoint}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setMsg({
        type: 'success',
        text: `Importados ${res.data.imported} registros correctamente.`,
      })
      // refrescar la lista pertinente
      if (endpoint === 'actions') fetchActionCodes()
      if (endpoint === 'results') fetchResultCodes()
      if (endpoint === 'contacts') fetchContactCodes()
    } catch (e) {
      setMsg({
        type: 'error',
        text: e.response?.data?.message || 'Error al procesar el archivo',
      })
    }
  }

  // — Diálogo para CRUD individual
  const openCreateDialog = (type) => {
    setEditingType(type)
    setIsEditing(false)
    setCurrent({ id: null, code: '', label: '' })
    setOpenDialog(true)
    setMsg({ type: '', text: '' })
  }
  const openEditDialog = (type, item) => {
    setEditingType(type)
    setIsEditing(true)
    setCurrent({ id: item.id, code: item.code, label: item.label })
    setOpenDialog(true)
    setMsg({ type: '', text: '' })
  }
  const handleDialogClose = () => {
    setOpenDialog(false)
    setCurrent({ id: null, code: '', label: '' })
    setMsg({ type: '', text: '' })
  }

  const handleSave = async () => {
    if (!current.code.trim() || !current.label.trim()) {
      setMsg({ type: 'error', text: 'Código y etiqueta son obligatorios.' })
      return
    }
    const endpoint =
      editingType === 'action'
        ? 'actions'
        : editingType === 'result'
        ? 'results'
        : 'contacts'
    const payload = {
      code: current.code.trim(),
      label: current.label.trim(),
    }
    try {
      if (isEditing) {
        await api.put(`/codes/${endpoint}/${current.id}`, payload)
        setMsg({ type: 'success', text: 'Actualizado correctamente.' })
      } else {
        await api.post(`/codes/${endpoint}`, payload)
        setMsg({ type: 'success', text: 'Creado correctamente.' })
      }
      handleDialogClose()
      // recargar
      if (endpoint === 'actions') fetchActionCodes()
      if (endpoint === 'results') fetchResultCodes()
      if (endpoint === 'contacts') fetchContactCodes()
    } catch (e) {
      setMsg({
        type: 'error',
        text: e.response?.data?.message || 'Error al guardar el código.',
      })
    }
  }

  const handleDelete = async (endpoint, id) => {
    if (!window.confirm('¿Eliminar este código?')) return
    try {
      await api.delete(`/codes/${endpoint}/${id}`)
      setMsg({ type: 'success', text: 'Eliminado correctamente.' })
      if (endpoint === 'actions') fetchActionCodes()
      if (endpoint === 'results') fetchResultCodes()
      if (endpoint === 'contacts') fetchContactCodes()
    } catch {
      setMsg({ type: 'error', text: 'Error al eliminar el código.' })
    }
  }

  return (
    <PrivatePageSection
      title="Administrar Códigos de Acción / Contacto / Resultado"
      actions={
        <Button
          variant="outlined"
          startIcon={<Download />}
          component="a"
          href="/templates/Codigos_accion_contacto_resultado.xlsx"
          download
          fullWidth={false}
        >
          Descargar plantilla
        </Button>
      }
    >

      {msg.text && (
        <Alert severity={msg.type} sx={{ mb: 2 }}>
          {msg.text}
        </Alert>
      )}

      {/* — Sección Acción */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Códigos de Acción
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Box>
            <input
              type="file"
              accept=".csv,.txt,.xls,.xlsx"
              onChange={(e) => setFileA(e.target.files[0])}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<UploadFile />}
            onClick={() => handleUpload('actions', fileA)}
          >
            Subir Archivo
          </Button>
          <Button
            variant="outlined"
            onClick={() => openCreateDialog('action')}
          >
            Crear Código
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Etiqueta</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {actionCodes.length > 0 ? (
              actionCodes.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.code}</TableCell>
                  <TableCell>{a.label}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEditDialog('action', a)}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete('actions', a.id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No hay acciones registradas
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* — Sección Resultado */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Códigos de Resultado
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Box>
            <input
              type="file"
              accept=".csv,.txt,.xls,.xlsx"
              onChange={(e) => setFileR(e.target.files[0])}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<UploadFile />}
            onClick={() => handleUpload('results', fileR)}
          >
            Subir Archivo
          </Button>
          <Button
            variant="outlined"
            onClick={() => openCreateDialog('result')}
          >
            Crear Código
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Etiqueta</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resultCodes.length > 0 ? (
              resultCodes.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.code}</TableCell>
                  <TableCell>{r.label}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEditDialog('result', r)}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete('results', r.id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No hay resultados registrados
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* — Sección Contacto */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Códigos de Contacto
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Box>
            <input
              type="file"
              accept=".csv,.txt,.xls,.xlsx"
              onChange={(e) => setFileC(e.target.files[0])}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<UploadFile />}
            onClick={() => handleUpload('contacts', fileC)}
          >
            Subir Archivo
          </Button>
          <Button
            variant="outlined"
            onClick={() => openCreateDialog('contact')}
          >
            Crear Código
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Etiqueta</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contactCodes.length > 0 ? (
              contactCodes.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.code}</TableCell>
                  <TableCell>{c.label}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEditDialog('contact', c)}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete('contacts', c.id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No hay contactos registrados
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* — Diálogo de creación/edición */}
      <Dialog open={openDialog} onClose={handleDialogClose} fullWidth>
        <DialogTitle>
          {isEditing ? 'Editar ' : 'Crear '}
          {editingType === 'action'
            ? 'Código de Acción'
            : editingType === 'result'
            ? 'Código de Resultado'
            : 'Código de Contacto'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Código"
            value={current.code}
            onChange={(e) => setCurrent({ ...current, code: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Etiqueta"
            value={current.label}
            onChange={(e) => setCurrent({ ...current, label: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </PrivatePageSection>
  )
}

export default CodesManagement
